<div class="profile-page">
  <!-- Identity strip: gives the tab a subject instead of opening straight onto
       two bare form columns. -->
  <header class="identity">
    <div class="identity__text">
      <h2 class="identity__name">{{ fullName || "Your account" }}</h2>
      <p class="identity__email" [vngTooltip]="profileData?.email || ''" vngTooltipPosition="bottom">
        {{ profileData?.email }}
      </p>
    </div>
    <vng-badge *ngIf="profileData" class="identity__badge" [variant]="signInMethodBadge.variant">
      {{ signInMethodBadge.label }}
    </vng-badge>
  </header>

  <div class="profile-grid">
    <!-- ── Personal information ─────────────────────────────────────────── -->
    <section class="settings-card" aria-labelledby="personal-info-heading">
      <div class="settings-card__header">
        <span class="settings-card__icon" aria-hidden="true">
          <vng-icon name="person" [outline]="false" [size]="20"></vng-icon>
        </span>
        <div>
          <h3 id="personal-info-heading" class="settings-card__title">Personal Information</h3>
          <p class="settings-card__subtitle">The name shown on your FlyCuts, invitations and shared templates.</p>
        </div>
      </div>

      <form class="settings-card__body" [formGroup]="form" (ngSubmit)="form.valid && updateProfile(form.value)">
        <label for="amex" style="display: none">AMEX</label>
        <input type="hidden" name="amex" required="" value="" id="amex" />

        <div class="field-row">
          <vng-input label="First Name" placeholder="First Name" class="field-row__item"
            [formControl]="form.controls['first_name']" [flyMsgIgnore]="true" [error]="
              !form.controls['first_name'].touched
                ? ''
                : form.controls['first_name'].hasError('required')
                  ? 'First name is required'
                  : form.controls['first_name'].hasError('pattern')
                    ? 'Numbers not allowed.'
                    : ''
            "></vng-input>

          <vng-input label="Last Name" placeholder="Last Name" class="field-row__item"
            [formControl]="form.controls['last_name']" [flyMsgIgnore]="true" [error]="
              !form.controls['last_name'].touched
                ? ''
                : form.controls['last_name'].hasError('required')
                  ? 'Last name is required'
                  : form.controls['last_name'].hasError('pattern')
                    ? 'Numbers not allowed.'
                    : ''
            "></vng-input>
        </div>

        <!-- The email is read-only here; say so instead of leaving a greyed-out
             field with no explanation. The note is hand-rendered rather than
             passed to `[hint]` because that input takes a plain string and this
             one carries a link. -->
        <div>
          <vng-input label="Email" placeholder="Email" class="w-100" [reserveSubscript]="false"
            [value]="form.controls['email'].value" [disabled]="true" [vngTooltip]="form.controls['email'].value || ''"
            vngTooltipPosition="top"></vng-input>
          <p class="field-note">
            Your email is your sign-in identity and can't be changed here —
            <a target="_blank" rel="noopener noreferrer" href="https://vengreso.com/flymsg/feedback">contact support</a>
            if you need it updated.
          </p>
        </div>

        <div class="settings-card__actions">
          <span class="settings-card__actions-hint" *ngIf="form.dirty && form.valid">You have unsaved changes.</span>
          <button type="submit" vng-button variant="primary" [loading]="updating" [disabled]="!canSaveProfile">
            Save Changes
          </button>
        </div>
      </form>
    </section>

    <!-- ── Password ─────────────────────────────────────────────────────── -->
    <section class="settings-card" aria-labelledby="password-heading">
      <div class="settings-card__header">
        <span class="settings-card__icon" aria-hidden="true">
          <vng-icon name="lock" [outline]="false" [size]="20"></vng-icon>
        </span>
        <div>
          <h3 id="password-heading" class="settings-card__title">Password &amp; Security</h3>
          <p class="settings-card__subtitle">
            {{
            profileData?.is_password_set
            ? "You'll be signed out of FlyMSG after changing your password."
            : "How you sign in to FlyMSG."
            }}
          </p>
        </div>
      </div>

      <!-- Google-only accounts have no password to change. The card used to
           disappear entirely, leaving the tab visibly lopsided with no
           explanation. -->
      <div class="settings-card__body" *ngIf="profileData && !profileData.is_password_set">
        <div class="notice notice--info">
          <vng-icon name="info" [outline]="false" [size]="20"></vng-icon>
          <p>
            You sign in to FlyMSG with <strong>Google</strong>, so there's no FlyMSG password to manage. Your password
            is
            handled by your Google account.
          </p>
        </div>
      </div>

      <form class="settings-card__body" *ngIf="profileData?.is_password_set" [formGroup]="pform"
        (ngSubmit)="pform.valid && updatePassword(pform.value)">
        <label for="amex" style="display: none">AMEX</label>
        <input type="hidden" name="amex" required="" value="" id="amex" />

        <vng-input label="Old Password" placeholder="Old Password" class="w-100"
          [type]="showOldPassword ? 'text' : 'password'" [formControl]="pform.controls['old_password']"
          [flyMsgIgnore]="true" [error]="
            !pform.controls['old_password'].touched
              ? ''
              : pform.controls['old_password'].hasError('required')
                ? 'Password is required'
                : ''
          ">
          <button vngSuffix vng-button variant="ghost" size="sm" type="button"
            [attr.aria-label]="showOldPassword ? 'Hide old password' : 'Show old password'"
            (click)="showOldPassword = !showOldPassword" tabindex="-1">
            <vng-icon [name]="showOldPassword ? 'visibility_off' : 'visibility'" [outline]="false"></vng-icon>
          </button>
        </vng-input>

        <div>
          <vng-input label="New Password" placeholder="New Password" class="w-100 password-counter"
            [type]="showNewPassword ? 'text' : 'password'" [formControl]="pform.controls['new_password']"
            [flyMsgIgnore]="true" [hint]="(pform.controls['new_password'].value?.length || 0) + ' / 25'" hintAlign="end"
            [error]="
              !pform.controls['new_password'].touched
                ? ''
                : pform.controls['new_password'].hasError('required')
                  ? 'Password is required'
                  : pform.controls['new_password'].hasError('pattern')
                    ? 'Password is not valid'
                    : ''
            ">
            <button vngSuffix vng-button variant="ghost" size="sm" type="button"
              [attr.aria-label]="showNewPassword ? 'Hide new password' : 'Show new password'"
              (click)="showNewPassword = !showNewPassword" tabindex="-1">
              <vng-icon [name]="showNewPassword ? 'visibility_off' : 'visibility'" [outline]="false"></vng-icon>
            </button>
          </vng-input>

          <!-- C3-5 / 04 §3.1: the one VISIBLE password-strength bar in the app —
               composed from vng-progress-bar (no library gap component exists). -->
          <div class="strength">
            <vng-progress-bar class="strength__bar" [value]="passwordStrength(pform.controls['new_password'].value)"
              [variant]="
                passwordStrength(pform.controls['new_password'].value) < 40
                  ? 'error'
                  : passwordStrength(pform.controls['new_password'].value) < 100
                    ? 'warning'
                    : 'success'
              "></vng-progress-bar>
            <span class="strength__label">{{ passwordStrengthLabel(pform.controls['new_password'].value) }}</span>
          </div>

          <!-- V3-16: the unmet-rule badges must stay brand orange (`warning` =
               --vng-color-warn #f0955c), not `error` (--vng-color-destructive
               #cf3455) — crimson is off-palette on this page. `[size]` is
               explicit because vng-icon otherwise inherits .password-rules__row's
               13px font-size and inks a ~10px dot instead of master's 16px badge. -->
          <ul class="password-rules">
            <li class="password-rules__row" *ngFor="let rule of passwordRules(pform.controls['new_password'].value)">
              <vng-icon [name]="rule.met ? 'done' : 'error'" [color]="rule.met ? 'success' : 'warning'"
                [outline]="false" [size]="16"></vng-icon>
              <span [class.password-rules__row--met]="rule.met">{{ rule.label }}</span>
            </li>
          </ul>
        </div>

        <vng-input label="Confirm Password" placeholder="Confirm Password" class="w-100"
          [type]="showConfirmPassword ? 'text' : 'password'" [formControl]="pform.controls['new_password_confirmation']"
          [flyMsgIgnore]="true" [error]="
            !pform.controls['new_password_confirmation'].touched
              ? ''
              : pform.controls['new_password_confirmation'].hasError('required')
                ? 'Confirm password is required'
                : pform.controls['new_password_confirmation'].errors?.compare
                  ? 'Passwords do not match'
                  : ''
          ">
          <button vngSuffix vng-button variant="ghost" size="sm" type="button"
            [attr.aria-label]="showConfirmPassword ? 'Hide confirmed password' : 'Show confirmed password'"
            (click)="showConfirmPassword = !showConfirmPassword" tabindex="-1">
            <vng-icon [name]="showConfirmPassword ? 'visibility_off' : 'visibility'" [outline]="false"></vng-icon>
          </button>
        </vng-input>

        <div class="settings-card__actions">
          <button type="submit" vng-button variant="primary" [disabled]="pform.invalid">Update Password</button>
        </div>
      </form>
    </section>
  </div>

  <!-- ── Danger zone ────────────────────────────────────────────────────── -->
  <section class="danger-zone" aria-labelledby="danger-zone-heading">
    <div class="danger-zone__header">
      <span class="danger-zone__icon" aria-hidden="true">
        <vng-icon name="warning" [outline]="false" [size]="20"></vng-icon>
      </span>
      <div>
        <h3 id="danger-zone-heading" class="danger-zone__title">Danger Zone</h3>
        <p class="danger-zone__subtitle">Irreversible actions. Please read carefully before continuing.</p>
      </div>
    </div>

    <!-- Deliberately just the trigger: the consequence list, the "no undo"
         notice and the required reason all live in the dialog, where the user
         reads them at the moment of the decision. -->
    <div class="danger-zone__body">
      <div class="danger-zone__row">
        <div>
          <h4 class="danger-zone__action-title">Delete your account</h4>
          <p class="danger-zone__lede">
            Permanently erases your account and everything in it. This cannot be undone.
          </p>
        </div>
        <button id="deletePopup" type="button" vng-button variant="destructive" [loading]="deleting"
          (click)="deleteProfile()">
          <vng-icon name="delete" [outline]="false" [size]="18"></vng-icon>
          Delete Profile
        </button>
      </div>
    </div>
  </section>
</div>